import React, { useEffect, useRef, useState, useContext } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "../ui/button";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getPublicFileUrl } from "../../config";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useChatOptimistic } from "../../context/ChatOptimistic/ChatOptimisticContext";

// Cache em memória FRONTEND (camada 0) para NUNCA mais mostrar tela
// branca / spinner de loader nas próximas aberturas do painel. Vida curta
// igual ao cache LRU do backend (180s) para não ficar com figurinhas velhas
// se alguém adicionar/apagar em outra aba.
const TTL_CACHE_FRONTEND_MS = 180 * 1000;
let cacheMemFigurinhas = {
	valor: null,
	expiraEm: 0,
};
const gravarCacheFrontend = (items) => {
	cacheMemFigurinhas = {
		valor: Array.isArray(items) ? [...items] : [],
		expiraEm: Date.now() + TTL_CACHE_FRONTEND_MS,
	};
};
const lerCacheFrontendValido = () => {
	if (!Array.isArray(cacheMemFigurinhas.valor)) return null;
	if (Date.now() > cacheMemFigurinhas.expiraEm) return null;
	return cacheMemFigurinhas.valor;
};
export const invalidarCacheFrontendFigurinhas = () => {
	cacheMemFigurinhas = { valor: null, expiraEm: 0 };
};

// Painel de figurinhas do atendimento.
//
// O que o servidor guarda é a figurinha já convertida (webp 512): a pessoa
// escolhe qualquer foto do computador e a conversão acontece no envio, porque
// o WhatsApp recusa figurinha que não seja webp.
//
// Modo de posicionamento:
//   - Por padrão (sem `ancoraRef`) é um `absolute bottom-full` normal, filhO
//     do MessageInput e posicionado por CSS (para casos simples).
//   - Quando `ancoraRef` vem do MessageInput, o painel é movido por
//     `createPortal` para o `<body>`. O motivo é que o wrapper do
//     MessageInput tem `@container` que cria um stacking context CONFINADO e
//     pintava o painel POR TRÁS dos cards de mensagem irmãos ("ainda abre
//     atrás e não consigo usar"). Com portal ele fica livre e z-index só
//     compete com o resto da página. A posição visual é mantida via
//     `getBoundingClientRect` da âncora + `scrollY/scrollX`.
const FigurinhaPicker = ({ aberto, aoFechar, ticketId, desabilitado, ancoraRef, quotedMsg, customOnSelectSticker }) => {
	// ================================================================
	// FALLBACK de contextos para ambientes FORA da árvore de Tickets
	// (ex: InternalChatWidget popup chat interno, Página /internal-chat).
	//
	// - ReplyMessageContext: valor default do createContext é undefined.
	//   Fora de ReplyMessageProvider → useContext retorna undefined.
	//   Solução: operador || {} garante objeto vazio em vez de undefined,
	//   desestruturação NUNCA mais crasha.
	//
	// - useChatOptimistic(): o hook JÁ TEM fallback seguro EMBUTIDO
	//   (ver ChatOptimisticContext.jsx L224-L233). Se ctx for null, retorna
	//   { adicionarMensagem: () => `opt_noop_${Date.now()}`, atualizarMensagem:
	//   () => {}, removerMensagem: () => {}, ... } com NOOPs seguros.
	//   → Podemos chamar DIRETO, sem try/catch, sem wrap em função (isso
	//     quebrava as Rules of Hooks no StrictMode).
	//
	// No fluxo customOnSelectSticker (InternalChat) NÃO usamos os valores
	// de contexto anyway: quotedMsg vem por prop e optimistic update é
	// manual pelo caller. Então defaults vazios são 100% seguros.
	// ================================================================
	const replyCtx = useContext(ReplyMessageContext) || {};
	const { replyingMessage: replyingMessageDoContexto, setReplyingMessage } = replyCtx;

	const { user: usuarioLogado } = useContext(AuthContext);

	// Hook normal SEM wrap em try/catch ou IIFE — cumpre Rules of Hooks.
	// O próprio hook já retorna NOOPs seguros quando fora do Provider.
	const { adicionarMensagem, atualizarMensagem, substituirMensagem, removerMensagem } = useChatOptimistic();
	// Lazy init: tenta puxar do cache de memória na MONTA (cada aberto=true
	// remonta o componente por conta do `if (!aberto) return null`). Desta
	// forma NÃO precisamos chamar `setFigurinhas` sincrono dentro do effect
	// (que gerava cascata de re-renders proibida pela regra react-hooks).
	const [figurinhas, setFigurinhas] = useState(() => lerCacheFrontendValido() || []);
	const [carregando, setCarregando] = useState(() => (lerCacheFrontendValido() ? false : true));
	const [enviando, setEnviando] = useState(null);
	const [subindo, setSubindo] = useState(false);
	const painelRef = useRef(null);
	const arquivoRef = useRef(null);
	const [, setTick] = useState(0);

	// Reposiciona quando a âncora é passada e a janela mexe.
	useEffect(() => {
		if (!aberto) return;
		if (!ancoraRef?.current) return;
		const recalcular = () => setTick((t) => (t + 1) % 1000000);
		recalcular();
		window.addEventListener("resize", recalcular);
		window.addEventListener("scroll", recalcular, true);
		const ob = typeof ResizeObserver !== "undefined"
			? new ResizeObserver(recalcular)
			: null;
		ob?.observe(ancoraRef.current);
		return () => {
			window.removeEventListener("resize", recalcular);
			window.removeEventListener("scroll", recalcular, true);
			ob?.disconnect();
		};
	}, [aberto, ancoraRef]);

	useEffect(() => {
		if (!aberto) return;

		const cache = lerCacheFrontendValido();
		const fundoRapido = Array.isArray(cache) && cache.length > 0;
		// Se chegou aqui com cache, a inicialização lazy do useState já pintou
		// os cards (carregando=false) e não precisa setState nenhum. Sem cache
		// o lazy init também já deixou carregando=true. Qualquer setState
		// SINCRONO no corpo do effect é proibido pela regra react-hooks
		// (cascata de re-renders). Então nenhum setState aqui antes da IIFE.

		(async () => {
			try {
				const { data } = await api.get("/figurinhas");
				const items = Array.isArray(data) ? data : [];
				gravarCacheFrontend(items);
				setFigurinhas(items);
			} catch (err) {
				if (!fundoRapido) toastError(err);
			} finally {
				setCarregando(false);
			}
		})();
	}, [aberto]);

	// Clicar fora fecha, como o seletor de emoji ao lado.
	useEffect(() => {
		if (!aberto) return undefined;

		const aoClicarFora = (e) => {
			if (painelRef.current && !painelRef.current.contains(e.target)) {
				aoFechar();
			}
		};

		document.addEventListener("mousedown", aoClicarFora);
		return () => document.removeEventListener("mousedown", aoClicarFora);
	}, [aberto, aoFechar]);

	const enviar = async (figurinha) => {
		const mensagemParaResponder =
			quotedMsg?.id ? quotedMsg : replyingMessageDoContexto;

		const quotedMsgLimpo = mensagemParaResponder?.id
			? {
					id: mensagemParaResponder.id,
					fromMe: Boolean(mensagemParaResponder.fromMe),
					body: mensagemParaResponder.body || "",
				}
			: null;

		// ================================================================
		// customOnSelectSticker (NOVO optional prop): usado por InternalChat,
		// InternalChatWidget, páginas de chat que NÃO são Tickets e não
		// tem rota /figurinhas/:id/enviar nem ChatOptimisticContext.
		//
		// Quando a prop customOnSelectSticker FOR PASSADA, este código roda
		// SEMPRE — e NUNCA executamos o fallback do fluxo de Tickets abaixo,
		// não importa o que o custom retorne.
		//
		// Motivo do BUG "ERR_NO_TICKET_ID HTTP 500" reportado:
		// Antes, se customOnSelectSticker retornava `false` (ex.: fetch da
		// figurinha falhou por CORS/network), o código caía no fallback do
		// fluxo de Tickets (L206+) que chama POST `/figurinhas/:id/enviar`
		// com `ticketId = undefined` (pois chat interno não tem ticketId).
		// O backend dessa rota SEMPRE exige ticketId e estourava
		// HttpException 500 ERR_NO_TICKET_ID, mesmo que o problema real
		// fosse apenas uma falha de download do blob.
		//
		// Regra ABSOLUTA (não negociavel):
		//   Se prop customOnSelectSticker existir → SEMPRE retorna depois
		//   do bloco, nunca toca no fluxo de Tickets / ChatOptimistic.
		//   Se prop NÃO existir → fluxo original de Tickets executa 100%
		//   igual a antes (retrocompatibilidade 100%).
		//
		// Retorno esperado do callback (ainda válido apenas para UX):
		//   true  → sucesso.
		//   false/undefined → falhou (mostramos toast, não tentamos mais nada
		//                      porque a própria função custom já deve ter
		//                      notificado o usuário do erro interno).
		//   Promise<boolean> → await e depois aplica a regra acima.
		// ================================================================
		if (typeof customOnSelectSticker === "function") {
			setEnviando(figurinha.id);
			try {
				const res = customOnSelectSticker(figurinha, {
					quotedMsg: quotedMsgLimpo,
					usuarioLogado,
				});
				const ok = typeof res?.then === "function" ? await res : res;
				if (ok !== false) {
					// custom enviou com sucesso → fecha
					if (typeof setReplyingMessage === "function") setReplyingMessage(null);
					aoFechar();
				}
				// ================================================================
				// NÃO CAIA MAIS AQUI PARA O FLUXO DE TICKETS!
				// custom já rodou — seja true ou false, acabou.
				// Retorno aqui para não executar L206+ (rota /figurinhas/:id/enviar
				// que requer ticketId e crasha com ERR_NO_TICKET_ID no chat
				// interno).
				// ================================================================
				return;
			} catch (err) {
				toastError(err);
				return;
			} finally {
				setEnviando(null);
			}
		}

		// ========== FLUXO ORIGINAL DE TICKETS (só roda SE customOnSelectSticker
		//            NÃO foi passado como prop — retrocompatibilidade 100%). =====

		// GUARD DEFENSIVO EXTRA: se por algum motivo chegamos aqui (não usamos
		// customOnSelectSticker) MAS ticketId é nulo/vazio → não há como enviar
		// para a rota de tickets, abortar com erro amigável sem crashar.
		// Isso previne HTTP 500 ERR_NO_TICKET_ID em qualquer cenário futuro
		// onde FigurinhaPicker for usado sem ticketId e sem customOnSelectSticker.
		if (ticketId == null || ticketId === "" || Number.isNaN(Number(ticketId))) {
			toastError(new Error("Ticket não selecionado ou inválido ao enviar figurinha (ticketId vazio)."));
			return;
		}

		// UX: feedback IMEDIATO no momento do clique — 3 coisas acontecem
		// DENTRO do frame de 16ms do clique:
		//   1) spinner na figurinha do painel;
		//   2) painel FECHADO;
		//   3) FIGURINHA RENDERIZADA NO TICKET (mensagem otimista).
		//
		// Só a etapa de rede (POST /enviar e confirmação do WhatsApp) acontece
		// depois. Em telas com scroll rápido, a sensação é "tão rápido quanto
		// o WhatsApp oficial".
		setEnviando(figurinha.id);

		const payload = { ticketId };
		if (quotedMsgLimpo) payload.quotedMsg = quotedMsgLimpo;

		// ============ Mensagem OTIMISTA =============
		const optimisticId = typeof adicionarMensagem === "function"
			? adicionarMensagem({
					mediaType: "sticker",
					mediaUrl: figurinha.mediaUrl || "",
					fromMe: true,
					userId: usuarioLogado?.id ?? null,
					quotedMsg: quotedMsgLimpo,
					ticketId: Number(ticketId),
					extra: {
						// Ajudar o render do balão a saber que é figurinha
						user: usuarioLogado
							? {
									id: usuarioLogado.id,
									name: usuarioLogado.name,
									profile: usuarioLogado.profile,
								}
							: null,
					},
				})
			: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

		aoFechar();

		try {
			const { data: respostaEnviar } = await api.post(
				`/figurinhas/${figurinha.id}/enviar`,
				payload,
			);

			// Backend retorna HTTP 200 com apenas `{ id }` (bigint do banco, sem
			// a mensagem completa). Não podemos substituir cegamente a otimista
			// por { id: 123 } — se o socket eco `appMessage.create` chegar
			// DEPOIS, antes da gente ter a mensagem completa, a UI mescla tudo
			// no reducer ADD_MESSAGE graças ao dedupe POR ID implementado
			// (mesmo id não cria nova). Então aqui só precisamos MARCAR a
			// otimista com o ID REAL (para o match por id do reducer funcionar
			// de forma 100% certa quando o eco chegar com dados full de
			// mediaUrl, mediaType, ack, user, quotedMsg etc.).
			const idReal =
				(respostaEnviar && typeof respostaEnviar === "object" && respostaEnviar?.id) ||
				respostaEnviar ||
				null;

			if (idReal && String(idReal) && !String(idReal).startsWith("opt_")) {
				if (typeof atualizarMensagem === "function") {
					atualizarMensagem(
						optimisticId,
						{
							id: idReal,
							// Continua isOptimistic=true por ~500ms no máximo até o
							// eco do socket appMessage.create chegar (que traz os
							// dados completos e zera isOptimistic). Se por algum
							// motivo o eco falhar, o próximo LOAD_MESSAGES do F5
							// vai trazer a mensagem real e o dedupe por id também
							// mescla.
						},
						{ marcarComoConfirmada: false },
					);
				}
			}

			if (typeof setReplyingMessage === "function") setReplyingMessage(null);
		} catch (err) {
			// Falhou: remove a otimista para não ficar "enviando" para sempre.
			// O toastError já notifica o usuário do erro.
			if (typeof removerMensagem === "function") removerMensagem(optimisticId);
			toastError(err);
		} finally {
			setEnviando(null);
		}
	};

	const adicionar = async (e) => {
		const arquivos = Array.from(e.target.files || []);

		if (arquivos.length === 0) return;

		const dados = new FormData();
		arquivos.forEach((arquivo) => dados.append("medias", arquivo));

		setSubindo(true);
		try {
			const { data } = await api.post("/figurinhas", dados);
			const items = Array.isArray(data) ? data : [];
			gravarCacheFrontend(items);
			invalidarCacheFrontendFigurinhas();
			setFigurinhas(items);
			toast.success(
				arquivos.length > 1 ? "Figurinhas criadas." : "Figurinha criada.",
			);
		} catch (err) {
			toastError(err);
		}
		setSubindo(false);
		// Sem isto, escolher o mesmo arquivo de novo não dispara o onChange.
		e.target.value = "";
	};

	const remover = async (figurinha, e) => {
		e.stopPropagation();

		try {
			await api.delete(`/figurinhas/${figurinha.id}`);
			invalidarCacheFrontendFigurinhas();
			setFigurinhas((atual) => atual.filter((f) => f.id !== figurinha.id));
		} catch (err) {
			toastError(err);
		}
	};

	if (!aberto) return null;

	// ================================================================
	// POSICIONAMENTO SEMPRE POR CREATEPORTAL.
	//
	// Motivo do BUG "clico em figurinhas NÃO ABRE":
	// Antes, quando ancoraRef?.current === null/undefined, o código caía
	// em:
	//   wrapperClasses = `absolute bottom-full left-2 right-2 mb-2 ...`
	// e retornava `painel` INLINE (sem createPortal).
	//
	// O problema: NA PÁGINA /internal-chat E NO POPUP CHAT INTERNO,
	// o container PAI do FigurinhaPicker tem `overflow: hidden` +
	// `@container` stacking context → o painel ABSOLUTO era pintado
	// POR TRÁS (ou recortado pelas bordas do container) e o usuário
	// NÃO VIA NADA, embora o React tivesse renderizado corretamente
	// (por isso no console não aparecia erro nenhum).
	//
	// Solução: SEMPRE usar createPortal(document.body) para pintar o
	// painel FORA da árvore de containers com overflow:hidden. Se
	// ancoraRef existir, posicionamos relativo à âncora (comportamento
	// original). Se ancoraRef NÃO existir (ex: alguém usou sem passar
	// prop ou ref ainda não foi setado), usamos fallback de posicionar
	// 16px do canto inferior direito da viewport como WhatsApp.
	// ================================================================
	const baseClasses =
		"rounded-lg border bg-popover p-3 shadow-lg sm:w-[340px]";
	const wrapperStyle = {};
	let wrapperClasses = "";
	// Sempre usa portal, a menos que typeof document seja undefined (SSR).
	const usePortal = typeof document !== "undefined";

	if (ancoraRef?.current && typeof ancoraRef.current.getBoundingClientRect === "function") {
		// Âncora válida → mesmo cálculo de antes, alinhado ao botão
		// (espaço acima / abaixo + flip quando não couber).
		const rect = ancoraRef.current.getBoundingClientRect();
		const sm = typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches;
		const minHeight = 240;
		const espacoAcima = rect.top;
		const viewH = typeof window !== "undefined" ? window.innerHeight : 900;
		const viewW = typeof window !== "undefined" ? window.innerWidth : 1200;
		const espacoAbaixo = viewH - rect.bottom;
		const flipParaBaixo = espacoAcima < minHeight && espacoAbaixo > espacoAcima;
		const gapPx = 8;
		// POSICIONAMENTO FIXO (relativo à VIEWPORT, não ao documento).
		// IMPORTANTE: position:fixed NÃO SOMA window.scrollX/scrollY, pois
		// getBoundingClientRect já retornou coordenadas relativas à viewport.
		// Antes estava somando scrollX/Y, o que deslocava o painel para fora
		// da tela quando a página tinha scroll.
		wrapperStyle.position = "fixed";
		// Z-INDEX BEM ALTO para garantir que pinta acima de TUDO — até mesmo
		// acima do popup do Chat interno do header (que já usa z-index alto),
		// modais, dropdowns, popovers, reaction quick bar etc.
		// 9999 deve ser maior que qualquer outro z-index do app.
		wrapperStyle.zIndex = 9999;
		if (flipParaBaixo) {
			wrapperStyle.top = `${rect.bottom + gapPx}px`;
			wrapperStyle.maxHeight = `calc(${espacoAbaixo}px - env(safe-area-inset-bottom) - 16px)`;
		} else {
			wrapperStyle.bottom = `${viewH - rect.top + gapPx}px`;
			wrapperStyle.maxHeight = `calc(${rect.top}px - env(safe-area-inset-top) - 16px)`;
		}
		// Esquerda e direita também são viewport relativas (fixed) → sem scrollX.
		const larguraMinima = 320;
		let leftPx = rect.left;
		let rightPx = viewW - rect.right;
		// Ajustar para não sair da viewport
		if (leftPx < 8) {
			leftPx = 8;
			rightPx = Math.max(8, viewW - leftPx - Math.max(larguraMinima, rect.width));
		}
		const maxRight = viewW - 8;
		if ((leftPx + (sm ? 340 : rect.width)) > maxRight) {
			leftPx = Math.max(8, maxRight - (sm ? 340 : Math.max(larguraMinima, rect.width)));
		}
		wrapperStyle.left = sm ? `${leftPx}px` : `${leftPx}px`;
		wrapperStyle.right = sm ? "auto" : `${Math.max(8, viewW - (leftPx + Math.max(larguraMinima, rect.width)))}px`;
		wrapperStyle.maxWidth = sm ? undefined : `${Math.max(240, rect.width - 16)}px`;
		wrapperClasses = baseClasses;
	} else {
		// Fallback: sem âncora válida → posiciona 16px do inferior direito
		// (igual WhatsApp desktop). Assim o painel SEMPRE aparece mesmo
		// sem ancoraRef ou em cenários de ref ainda nula.
		wrapperStyle.position = "fixed";
		wrapperStyle.bottom = "16px";
		wrapperStyle.right = "16px";
		wrapperStyle.maxHeight = "min(520px, 70vh)";
		wrapperStyle.maxWidth = "min(340px, calc(100vw - 32px))";
		// Fallback também com z-index super alto para não ficar atrás do popup.
		wrapperStyle.zIndex = 9999;
		wrapperClasses = baseClasses;
	}

	const painel = (
		<div
			ref={painelRef}
			data-figurinha-picker-portal
			style={wrapperStyle}
			className={wrapperClasses}
		>
			<div className="mb-2 flex items-center justify-between">
				<span className="text-sm font-medium">Figurinhas</span>
				<input
					ref={arquivoRef}
					type="file"
					multiple
					accept="image/png,image/jpeg,image/webp,image/gif"
					className="hidden"
					onChange={adicionar}
				/>
				<Button
					size="sm"
					variant="ghost"
					disabled={subindo}
					onClick={() => arquivoRef.current?.click()}
				>
					{subindo ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Plus className="h-4 w-4" />
					)}
					Nova
				</Button>
			</div>

			{carregando ? (
				<div className="flex h-24 items-center justify-center">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : figurinhas.length === 0 ? (
				// Estado vazio que diz o próximo passo: uma grade vazia deixaria
				// a pessoa achando que o painel está quebrado.
				<p className="py-6 text-center text-sm text-muted-foreground">
					Nenhuma figurinha ainda.
					<br />
					Use <strong>Nova</strong> para transformar uma imagem em
					figurinha, ou salve uma que o cliente mandar pelo menu da
					mensagem.
				</p>
			) : (
				<div className="grid max-h-64 grid-cols-4 gap-2 overflow-y-auto">
					{figurinhas.map((figurinha) => (
						<div key={figurinha.id} className="group relative">
							<button
								type="button"
								disabled={desabilitado || enviando === figurinha.id}
								onClick={() => enviar(figurinha)}
								title={figurinha.name}
								className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border bg-background p-1 hover:bg-accent disabled:opacity-50"
							>
								{enviando === figurinha.id ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<img
										src={getPublicFileUrl(figurinha.mediaUrl)}
										alt={figurinha.name}
										loading="lazy"
										decoding="async"
										width="48"
										height="48"
										className="max-h-full max-w-full object-contain"
										onError={(e) => {
											try {
												const tgt = e.currentTarget;
												if (!tgt) return;
												const etapa = tgt.dataset.fallback || "0";
												if (etapa === "0") {
													tgt.dataset.fallback = "1";
													const m = tgt.getAttribute("src") || "";
													const relativa = m.includes("/public/")
														? `${m.slice(m.indexOf("/public/"))}`
														: "";
													if (relativa && relativa !== m) {
														tgt.src = relativa;
														return;
													}
													tgt.dataset.fallback = "2";
												}
												if (etapa === "1") {
													tgt.dataset.fallback = "2";
													const semTimestamp = (tgt.getAttribute("src") || "")
														.replace(/[?#].*$/, "");
													if (semTimestamp && semTimestamp !== tgt.getAttribute("src")) {
														tgt.src = semTimestamp;
														return;
													}
												}
												if (tgt.parentNode) {
													const fallback = document.createElement("div");
													fallback.setAttribute(
														"class",
														"flex h-full w-full items-center justify-center rounded-md border border-dashed border-muted-foreground/30 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground"
													);
													fallback.setAttribute(
														"title",
														tgt.alt || "Figurinha indisponível"
													);
													fallback.textContent = "Figurinha";
													tgt.parentNode.replaceChild(fallback, tgt);
												}
											} catch {
												/* no-op: fallback de erro de carregamento, falhar silenciosamente */
											}
										}}
									/>
								)}
							</button>
							<button
								type="button"
								onClick={(e) => remover(figurinha, e)}
								aria-label="Apagar figurinha"
								title="Apagar figurinha"
								className="absolute -right-1 -top-1 hidden h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground group-hover:flex"
							>
								<Trash2 className="h-3 w-3" />
							</button>
						</div>
					))}
				</div>
			)}
		</div>
	);

	if (usePortal && typeof document !== "undefined") {
		return createPortal(painel, document.body);
	}
	return painel;
};

export default FigurinhaPicker;
